import React, {createContext, use, useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import {useQuery, useMutation} from "@apollo/client";
import PropTypes from "prop-types";
import GetOneRegulation from "./gql/getOneRegulation.graphql";
import UpdateOneRegulation from "./gql/UpdateOneRegulation.graphql";
import UpsertOneRegulatorLeader from "./gql/UpsertOneRegulatorLeader.graphql";
import UpdateOneNewsItem from "./gql/UpdateOneNewsItem.graphql";
import UpdateOneNotification from "./gql/UpdateOneNotification.graphql";
import UpdateOneTemplate from "./gql/NotificationTemplates/UpdateOneNotificationTemplate.graphql";
import DeleteOneTemplate from "./gql/NotificationTemplates/DeleteOneNotificationTemplate.graphql";
import CreateOneRegulation from "./gql/CreateOneRegulation.graphql";
import CreateOneNewsItem from "./gql/CreateOneNewsItem.graphql";
import CreateOneNotification from "./gql/CreateOneNotification.graphql";
import CreateOneTemplate from "./gql/NotificationTemplates/CreateOneNotificationTemplate.graphql";
import DeleteOneRegulatorLeader from "./gql/DeleteOneRegulatorLeader.graphql";
import SyncTagsForRegulation from "./gql/SyncTagsForRegulation.graphql";
import SyncGroupsForRegulation from "./gql/SyncGroupsForRegulation.graphql";
import GetTagTypes from "./gql/GetTagTypes.graphql";
import GetLegalTypes from "./gql/GetLegalTypes.graphql";
import dayjs from "dayjs";
import camelcase from "camelcase";
import {ALERT} from "../environment";
import {useNavigate} from "react-router-dom";
import {getStateByName, getCountryByName} from "../shared/geography";
import {each, isEmpty, map} from "underscore";

const notificationLabels = {
  REGULATOR: "Regulator",
  CONSUMER: "Consumer",
  CREDIT_RATING_AGENCY: "Consumer Reporting Agencies",
  SUBSTITUTE_NOTICE: "Substitute Notice",
};

const RegulationContext = createContext(undefined);

const getGeographiesAsRegulationTags = (data) => {
  const countryTags = map(data.regulationCountries, (countryName) => {
    return {
      tag: {
        type: {name: "Geography"},
        value: {
          geographyType: "Country",
          country: getCountryByName(countryName),
        },
      },
    };
  });
  const stateTags = map(data.regulationStates, (stateName) => {
    const state = getStateByName("US", stateName) ?? getStateByName("CA", stateName);

    return {
      tag: {
        type: {name: "Geography"},
        value: {
          geographyType: "State",
          state,
        },
      },
    };
  });

  return [...countryTags, ...stateTags];
};

const convertRegulationTagsToTags = (regulationTags) => {
  return map(regulationTags, (regulationTag) => {
    return {
      type: {
        name: regulationTag.tag.type.name,
      },
      value: JSON.stringify(regulationTag.tag.value),
    };
  });
};

const RegulationProvider = ({children}) => {
  const [regulation, setRegulation] = useState();
  const [tagTypes, setTagTypes] = useState([]);
  const [regulationLegalTypes, setLegalTypes] = useState([]);
  const [notificationTemplate, setNotificationTemplate] = useState([]);
  const [whichNotificationTemplateOpen, setWhichNotificationTemplateOpen] = useState("");
  const {id} = useParams();
  const navigate = useNavigate();

  const [updateRegulation, updateRegulationMethods] = useMutation(UpdateOneRegulation);

  useEffect(() => {
    if (updateRegulationMethods.data) ALERT.success("Regulation updated!");
    if (updateRegulationMethods.error) {
      ALERT.error("Error updating regulation");
      console.error(updateRegulationMethods.error);
    }
  }, [updateRegulationMethods.data, updateRegulationMethods.error]);

  const [createRegulation, createRegulationMethods] = useMutation(CreateOneRegulation);

  useEffect(() => {
    if (createRegulationMethods.data) {
      ALERT.success("Regulation created!");
      navigate(`/regulations/edit/${createRegulationMethods.data.createOneRegulation.id}`);
    }
    if (createRegulationMethods.error) {
      ALERT.error("Error creating regulation");
      console.error(createRegulationMethods.error);
    }
  }, [createRegulationMethods.data, createRegulationMethods.error, navigate]);

  const [upsertRegulatorLeader, upsertRegulatorLeaderMethods] = useMutation(
    UpsertOneRegulatorLeader,
    {
      awaitRefetchQueries: true,
      refetchQueries: [{query: GetOneRegulation, variables: {id: parseInt(id, 10)}}],
    },
  );

  useEffect(() => {
    if (upsertRegulatorLeaderMethods.data) ALERT.success("Regulator leader updated!");
    if (upsertRegulatorLeaderMethods.error) {
      ALERT.error("Error updating regulator leader");
      console.error(upsertRegulatorLeaderMethods.error);
    }
  }, [upsertRegulatorLeaderMethods.data, upsertRegulatorLeaderMethods.error]);

  const [deleteOneRegulatorLeader, deleteOneRegulatorLeaderMethods] = useMutation(
    DeleteOneRegulatorLeader,
    {
      awaitRefetchQueries: true,
      refetchQueries: [{query: GetOneRegulation, variables: {id: parseInt(id, 10)}}],
    },
  );

  useEffect(() => {
    if (deleteOneRegulatorLeaderMethods.data) ALERT.success("Regulator leader deleted!");
    if (deleteOneRegulatorLeaderMethods.error) {
      ALERT.error("Error deleting regulator leader");
      console.error(deleteOneRegulatorLeaderMethods.error);
    }
  }, [deleteOneRegulatorLeaderMethods.data, deleteOneRegulatorLeaderMethods.error]);

  const [createNewsItem, createNewsItemMethods] = useMutation(CreateOneNewsItem);
  const [updateNewsItem, updateNewsItemMethods] = useMutation(UpdateOneNewsItem);
  const [updateNotification, updateNotificationMethods] = useMutation(UpdateOneNotification);
  const [createNotification, createNotificationMethods] = useMutation(CreateOneNotification);

  useEffect(() => {
    if (createNewsItemMethods.error) {
      ALERT.error("Error creating news item");
      console.error(createNewsItemMethods.error);
    }
    if (updateNewsItemMethods.error) {
      ALERT.error("Error updating news item");
      console.error(updateNewsItemMethods.error);
    }
    if (updateNotificationMethods.error) {
      ALERT.error("Error updating notification");
      console.error(updateNotificationMethods.error);
    }
    if (createNotificationMethods.error) {
      ALERT.error("Error creating notification");
      console.error(createNotificationMethods.error);
    }
  }, [
    createNewsItemMethods.error,
    updateNewsItemMethods.error,
    updateNotificationMethods.error,
    createNotificationMethods.error,
  ]);

  const [syncTagsForRegulation, syncTagsForRegulationMethods] = useMutation(SyncTagsForRegulation);
  const [syncGroupsForRegulation, syncGroupsForRegulationMethods] =
    useMutation(SyncGroupsForRegulation);

  useEffect(() => {
    if (syncTagsForRegulationMethods.error) {
      ALERT.error("Error syncing tags for regulation");
      console.error(syncTagsForRegulationMethods.error);
    }
    if (syncGroupsForRegulationMethods.error) {
      ALERT.error("Error syncing groups for regulation");
      console.error(syncGroupsForRegulationMethods.error);
    }
  }, [syncTagsForRegulationMethods.error, syncGroupsForRegulationMethods.error]);

  const getRegulationQuery = useQuery(GetOneRegulation, {
    variables: {id: parseInt(id, 10)},
    skip: !id,
  });

  useEffect(() => {
    if (getRegulationQuery.data) {
      setNotificationTemplate(
        map(
          getRegulationQuery.data.regulation.notifications,
          (notification) => notification.notification,
        ),
      );
      setRegulation(getRegulationQuery.data.regulation);
    }
    if (getRegulationQuery.error) {
      ALERT.error("Error getting one regulation");
      console.error(getRegulationQuery.error);
    }
  }, [getRegulationQuery.data, getRegulationQuery.error]);

  const [createTemplate, createTemplateMethods] = useMutation(CreateOneTemplate, {
    awaitRefetchQueries: true,
    refetchQueries: [{query: GetOneRegulation, variables: {id: parseInt(id, 10)}}],
  });

  const [updateTemplate, updateTemplateMethods] = useMutation(UpdateOneTemplate, {
    awaitRefetchQueries: true,
    refetchQueries: [{query: GetOneRegulation, variables: {id: parseInt(id, 10)}}],
  });

  const [deleteTemplate, deleteTemplateMethods] = useMutation(DeleteOneTemplate, {
    awaitRefetchQueries: true,
    refetchQueries: [{query: GetOneRegulation, variables: {id: parseInt(id, 10)}}],
  });

  useEffect(() => {
    if (createTemplateMethods.error) {
      ALERT.error("Error creating one template");
      console.error(createTemplateMethods.error);
    }
    if (updateTemplateMethods.error) {
      ALERT.error("Error updating one template");
      console.error(updateTemplateMethods.error);
    }
    if (deleteTemplateMethods.error) {
      ALERT.error("Error deleting one template");
      console.error(deleteTemplateMethods.error);
    }
  }, [createTemplateMethods.error, updateTemplateMethods.error, deleteTemplateMethods.error]);

  const getTagTypesQuery = useQuery(GetTagTypes);

  useEffect(() => {
    if (getTagTypesQuery.data) setTagTypes(getTagTypesQuery.data.tagTypes);
    if (getTagTypesQuery.error) {
      ALERT.error("Error getting tag types");
      console.error(getTagTypesQuery.error);
    }
  }, [getTagTypesQuery.data, getTagTypesQuery.error]);

  const getLegalTypeQuery = useQuery(GetLegalTypes);

  useEffect(() => {
    if (getLegalTypeQuery.data) {
      setLegalTypes(map(getLegalTypeQuery.data.__type.enumValues, (enumValue) => enumValue.name));
    }
    if (getLegalTypeQuery.error) {
      ALERT.error("Error getting legal types");
      console.error(getLegalTypeQuery.error);
    }
  }, [getLegalTypeQuery.data, getLegalTypeQuery.error]);

  const loading =
    updateRegulationMethods.loading ||
    upsertRegulatorLeaderMethods.loading ||
    updateNewsItemMethods.loading ||
    updateNotificationMethods.loading ||
    getRegulationQuery.loading ||
    createNewsItemMethods.loading ||
    createNotificationMethods.loading ||
    createRegulationMethods.loading ||
    deleteOneRegulatorLeaderMethods.loading ||
    getTagTypesQuery.loading ||
    syncTagsForRegulationMethods.loading ||
    getLegalTypeQuery.loading ||
    syncGroupsForRegulationMethods.loading ||
    createTemplateMethods.loading ||
    updateTemplateMethods.loading ||
    deleteTemplateMethods.loading;

  const create = async (data) => {
    if (isEmpty(data.regulationCountries) && isEmpty(data.regulationStates)) {
      if (
        !window.confirm(
          "No geography tags selected. Without geography tags, this regulation will not " +
            "appear when users filter by jurisdiction. Continue anyway?",
        )
      ) {
        return;
      }
    }
    const {
      data: {createOneRegulation: createdRegulation},
    } = await createRegulation({
      variables: {
        isDraft: data.isDraft,
        regulator: data.regulator,
        description: data.description,
        obligationRegex: data.obligationRegex || null,
        exceptions: data.exceptions,
        source: data.source,
        penalties: data.penalties,
        piiDefinition: data.piiDefinition,
        otherRequirements: data.otherRequirements,
        legislationPassedAt: dayjs(data.legislationPassedAt),
        effectiveAt: dayjs(data.effectiveAt),
        legalType: data.legalType,
        typeName: data.regulationType,
      },
    });

    const geographyTags = getGeographiesAsRegulationTags(data);
    const parsedTags = convertRegulationTagsToTags([...data.regulationTags, ...geographyTags]);

    await syncTagsForRegulation({
      variables: {
        regulationId: createdRegulation.id,
        tags: parsedTags,
      },
    });

    await syncGroupsForRegulation({
      variables: {
        regulationId: createdRegulation.id,
        groups: data.groups,
      },
    });

    for (const notificationType of Object.keys(notificationLabels)) {
      const notificationContent = data[`${camelcase(notificationType)}Notification`];
      const notificationConditions = data[`${camelcase(notificationType)}Conditions`];

      await createNotification({
        variables: {
          regulationId: createdRegulation.id,
          content: notificationContent ?? "",
          conditions: notificationConditions ?? "",
          type: notificationType,
        },
      });
    }
  };

  const update = async (data) => {
    if (isEmpty(data.regulationCountries) && isEmpty(data.regulationStates)) {
      if (
        !window.confirm(
          "No geography tags selected. Without geography tags, this regulation will not " +
            "appear when users filter by jurisdiction. Continue anyway?",
        )
      ) {
        return;
      }
    }
    each(data?.newsItems, async (news) => {
      const newsItem = news.newsItem;
      if (news?.isDirty) {
        await updateNewsItem({
          variables: {
            id: newsItem.id,
            isDraft: newsItem.isDraft,
            publisher: newsItem.publisher,
            publishedAt: dayjs(newsItem.publishedAt).toISOString(),
            source: newsItem.source,
            title: newsItem.title,
          },
        });
      }
      if (news?.isNew) {
        if (!(isEmpty(newsItem.publisher) && isEmpty(newsItem.source) && isEmpty(newsItem.title))) {
          await createNewsItem({
            variables: {
              isDraft: newsItem.isDraft,
              publisher: newsItem.publisher,
              publishedAt: dayjs(newsItem.publishedAt).toISOString(),
              source: newsItem.source,
              title: newsItem.title,
              regulationId: data.id,
            },
          });
        }
      }
    });

    const geographyTags = getGeographiesAsRegulationTags(data);
    const parsedTags = convertRegulationTagsToTags([
      ...data.regulationTags.filter((regulationTag) => regulationTag.tag.type.name !== "Geography"),
      ...geographyTags,
    ]);
    await syncTagsForRegulation({
      variables: {
        regulationId: regulation.id,
        tags: parsedTags,
      },
    });

    await syncGroupsForRegulation({
      variables: {
        regulationId: regulation.id,
        groups: data.groups,
      },
    });

    for (const regulationNotification of regulation?.notifications) {
      const notification = regulationNotification.notification;

      const notificationContent = data[`${camelcase(notification.type)}Notification`];
      const notificationConditions = data[`${camelcase(notification.type)}Conditions`];

      await updateNotification({
        variables: {
          id: notification.id,
          content: notificationContent ?? "",
          conditions: notificationConditions ?? "",
        },
      });
    }

    await updateRegulation({
      awaitRefetchQueries: true,
      refetchQueries: [{query: GetOneRegulation, variables: {id: regulation.id}}],
      variables: {
        id: regulation.id,
        isDraft: data.isDraft,
        regulator: data.regulator,
        description: data.description,
        obligationRegex: data.obligationRegex || null,
        exceptions: data.exceptions,
        source: data.source,
        penalties: data.penalties,
        piiDefinition: data.piiDefinition,
        otherRequirements: data.otherRequirements,
        legislationPassedAt: dayjs(data.legislationPassedAt),
        effectiveAt: dayjs(data.effectiveAt),
        legalType: data.legalType,
        typeName: data.regulationType,
      },
    });
  };

  const updateRegulatorLeader = (leaderData) => {
    upsertRegulatorLeader({variables: {regulationId: regulation.id, ...leaderData}});
  };

  const deleteRegulatorLeader = () => {
    deleteOneRegulatorLeader({variables: {regulationId: regulation.id}});
  };

  const providerContent = {
    regulation,
    tagTypes,
    regulationLegalTypes,
    notificationLabels,
    notificationTemplate,
    whichNotificationTemplateOpen,
    setWhichNotificationTemplateOpen,
    create,
    update,
    loading,
    createTemplate,
    createTemplateMethods,
    updateRegulatorLeader,
    deleteRegulatorLeader,
    updateTemplate,
    updateTemplateMethods,
    deleteTemplate,
    deleteTemplateMethods,
  };

  return <RegulationContext value={providerContent}>{children}</RegulationContext>;
};

RegulationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useRegulation = () => {
  return use(RegulationContext);
};

export {useRegulation, RegulationProvider};
